import { memo, useState } from 'react';
import type { TFunction } from 'i18next';
import { Menu } from '@components/ui/Menu/Menu';
import { ClockIcon, ZapIcon } from '@utils/icons';
import { KebabButton } from '@components/ui/KebabButton/KebabButton';
import { MilestoneMarker } from '../MilestoneMarker';
import { HeaderDependencies } from './HeaderDependencies';
import { severityClassName, type RiskSeverity } from '../model/risk';
import { ProfessionPill } from '@components/ui/ProfessionPill/ProfessionPill';
import type { IMatrixColumn } from './progressMatrix';
import { columnEquals } from './columnEquals';
import styles from './matrixTable.module.css';

export const HEADER_ROW = 'header';
export const LABEL_COL = 'label';

/** On-time risk severity → colour class, using the shared severity thresholds
 *  (same buckets as the Daily List RiskBadges). Only rendered when level > 0,
 *  so 'none' never reaches here. */
const RISK_CLASS: Record<RiskSeverity, string> = {
  none: '', low: styles.riskLow as string, mid: styles.riskMid as string, high: styles.riskHigh as string,
};
const riskClass = (level: number): string => severityClassName(level, RISK_CLASS);

/** Settings menu on an activity header: the kebab button opens a menu whose "Show in
 *  scheduler" item deep-links the activity into the Scheduler. Clicks are stopped
 *  from bubbling so they don't also trigger the header's open-drawer handler. */
function ActivitySettingsMenu({
  taskId, t, onShowInScheduler,
}: {
  taskId: string;
  t: TFunction;
  onShowInScheduler?: (taskId: string) => void;
}) {
  // Mounted on first click (defaultOpen) so a header doesn't pay for a closed
  // Menu.Root while scrolled past. Clicks are stopped from bubbling so they
  // don't also trigger the header's open-drawer handler.
  const [mounted, setMounted] = useState(false);
  return (
    <span className={styles.gearWrap} onClick={(e) => e.stopPropagation()}>
      {mounted ? (
        <Menu.Root defaultOpen onOpenChange={(open) => { if (!open) setMounted(false); }}>
          <Menu.Trigger render={<KebabButton className={styles.gear} iconSize={16} aria-label={t('activities.tracker.settings')} />} />
          <Menu.Portal>
            <Menu.Positioner sideOffset={4} align="end">
              <Menu.Popup>
                <Menu.Item onClick={() => onShowInScheduler?.(taskId)}>
                  {t('activities.tracker.showInScheduler')}
                </Menu.Item>
              </Menu.Popup>
            </Menu.Positioner>
          </Menu.Portal>
        </Menu.Root>
      ) : (
        <KebabButton
          className={styles.gear}
          iconSize={16}
          aria-label={t('activities.tracker.settings')}
          aria-haspopup="menu"
          aria-expanded="false"
          onClick={() => setMounted(true)}
        />
      )}
    </span>
  );
}

/**
 * Activity column header: name + `assignee · %`, a settings kebab menu (top-right), and
 * a pinned bottom bar with the gold critical-path badge (left) and dependency
 * chips (right, hover → preview card). Clicking the header opens the activity
 * drawer. Group columns (Generation 1) are plain and non-interactive.
 */
export const ColumnHeader = memo(function ColumnHeader({
  col, colIndex, t, onOpen, onShowInScheduler, onJumpToTask,
}: {
  col: IMatrixColumn;
  /** Absolute column index in the full matrix (windowed rendering) — pins this
   *  header to its header-pane track (colIndex + 1; the pane holds only data
   *  columns, the label column is a separate pane). */
  colIndex: number;
  t: TFunction;
  onOpen?: (taskId: string) => void;
  onShowInScheduler?: (taskId: string) => void;
  /** The dependency-card rows' jump (scroll + flash) — see MatrixTable. */
  onJumpToTask?: (taskId: string, taskName: string) => void;
}) {
  const isActivity = !col.isGroup;
  return (
    // gridRow is explicit so the header is DEFINITELY placed: the jump-flash
    // overlay is a definite row-1 item too, and definite items may share a
    // cell — whereas an auto-row header colliding with the overlay's cell
    // would be evicted into an implicit second row, dragging every later
    // header down with it (sparse auto-placement never moves back up).
    <div
      className={`${styles.colHead} ${isActivity ? styles.colHeadActivity : ''}`}
      style={{ gridColumn: colIndex + 1, gridRow: 1 }}
      data-row={HEADER_ROW}
      data-col={col.taskId}
      onClick={isActivity ? () => onOpen?.(col.taskId) : undefined}
    >
      {/* Tooltip lives on the name, not the whole header, so it never pops over
          the dependency chips / preview card at the bottom of the cell. Shows the
          full title — useful now that long names are clamped to two lines. */}
      {/* Top group: title with the % / milestone stuck directly beneath it. */}
      <span className={styles.colTop}>
        <span className={styles.colName} title={col.name}>
          {col.name}
        </span>
        <span className={styles.colSub}>
          {col.progressPercent}%
          {col.isMilestone && <MilestoneMarker size={11} className={styles.milestoneSpacing} />}
        </span>
      </span>
      {/* Profession pushed to the bottom of the cell (space-between) — just above
          the pinned predecessors/successors bar. */}
      {col.profession && (
        <span className={styles.colAssignee}>
          <ProfessionPill profession={col.profession} compact />
        </span>
      )}
      {isActivity && <ActivitySettingsMenu taskId={col.taskId} t={t} onShowInScheduler={onShowInScheduler} />}
      {/* Bottom bar pinned to the cell's base: gold critical-path badge (left) +
          dep chips (right). Its clicks are stopped so interacting with the chips
          doesn't open the drawer. Header cells share one grid-row height, so this
          lands in the same spot on every column regardless of name wrap. */}
      {isActivity && (
        <span className={styles.bottomBar} onClick={(e) => e.stopPropagation()}>
          {/* Left stack: on-time risk badge (clock + level) sits above the gold
              critical-path badge. Both fit beside the dep chips in the narrow
              column, JA included. */}
          <span className={styles.leftStack}>
            {(col.onTimeRisk ?? 0) > 0 && (
              <span
                className={`${styles.risk} ${riskClass(col.onTimeRisk as number)}`}
                title={t('activities.card.onTime', { n: col.onTimeRisk })}
              >
                <ClockIcon size={12} />{col.onTimeRisk}
              </span>
            )}
            {col.isCritical && (
              <span className={styles.critTag} title={t('activities.criticalPath')} aria-label={t('activities.criticalPath')}>
                <ZapIcon size={12} />
              </span>
            )}
          </span>
          <span className={styles.depSlot}>
            <HeaderDependencies column={col} onJumpToTask={onJumpToTask} />
          </span>
        </span>
      )}
    </div>
  );
}, (prev, next) =>
  columnEquals(prev.col, next.col) &&
  prev.t === next.t &&
  prev.onOpen === next.onOpen &&
  prev.onShowInScheduler === next.onShowInScheduler &&
  prev.onJumpToTask === next.onJumpToTask &&
  prev.colIndex === next.colIndex,
);
